(() => {
  'use strict';

  const root = document.querySelector('[data-jungle-moc-2026]');
  if (!root) {
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const smallScreen = window.matchMedia('(max-width: 1023px)');
  const pageEntryScrollLockDuration = 1000;

  const initializePageEntry = () => {
    let unlockTimer = 0;
    let documentStyles = null;
    const scrollKeys = new Set([
      ' ',
      'Spacebar',
      'PageUp',
      'PageDown',
      'End',
      'Home',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
    ]);

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const preventScroll = (event) => {
      event.preventDefault();
    };

    const preventKeyboardScroll = (event) => {
      if (scrollKeys.has(event.key)) {
        event.preventDefault();
      }
    };

    const keepPageAtTop = () => {
      if (window.scrollX !== 0 || window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    };

    const unlockScroll = () => {
      if (!documentStyles) {
        return;
      }

      window.removeEventListener('wheel', preventScroll);
      window.removeEventListener('touchmove', preventScroll);
      window.removeEventListener('scroll', keepPageAtTop);
      document.removeEventListener('keydown', preventKeyboardScroll);
      document.documentElement.style.overflowY = documentStyles.overflowY;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, 0);
      document.documentElement.style.scrollBehavior = documentStyles.scrollBehavior;
      documentStyles = null;
    };

    const lockScrollAtTop = () => {
      window.clearTimeout(unlockTimer);
      unlockScroll();

      documentStyles = {
        overflowY: document.documentElement.style.overflowY,
        scrollBehavior: document.documentElement.style.scrollBehavior,
      };

      document.documentElement.style.scrollBehavior = 'auto';
      document.documentElement.style.overflowY = 'scroll';
      window.scrollTo(0, 0);
      window.addEventListener('wheel', preventScroll, { passive: false });
      window.addEventListener('touchmove', preventScroll, { passive: false });
      window.addEventListener('scroll', keepPageAtTop, { passive: true });
      document.addEventListener('keydown', preventKeyboardScroll);
    };

    // 初回表示時は、ファーストビューの縦線描画（イントロ→タイトル→本文の後に始まる）が
    // 終わるまでスクロールをロックする。縦線はイントロ完了まで動かないため、
    // 自然にイントロ完了後まで待つことになる。
    const releaseScrollLockAfterFirstView = () => {
      if (reducedMotion.matches) {
        unlockScroll();
        return;
      }

      const line = root.querySelector('[data-jungle-moc-2026-first-view-line]');
      if (!line) {
        unlockScroll();
        return;
      }

      line.addEventListener('animationend', unlockScroll, { once: true });
    };

    lockScrollAtTop();
    releaseScrollLockAfterFirstView();
    window.addEventListener('pagehide', unlockScroll);
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) {
        return;
      }

      // bfcacheからの復帰時はイントロ演出が再生されないため、短い固定時間でロックを解除する。
      lockScrollAtTop();
      unlockTimer = window.setTimeout(unlockScroll, pageEntryScrollLockDuration);
    });
  };

  const initializeIntroImages = () => {
    const introSection = root.querySelector('[data-jungle-moc-2026-intro]');
    if (!introSection) {
      return;
    }

    const finishIntro = () => {
      introSection.hidden = true;
      root.dispatchEvent(new CustomEvent('jungle-moc-2026:intro-complete'));
    };

    if (reducedMotion.matches) {
      finishIntro();
      return;
    }

    // 最後の画像のフェードイン完了を検知し、続くドッキング・退出アニメーションを開始する。
    const onLastImageFadeIn = (images, callback) => {
      if (images.length === 0) {
        finishIntro();
        return;
      }
      images[images.length - 1].addEventListener('animationend', callback, { once: true });
    };

    // 複数プロパティを同時にtransitionさせている要素で、完了を一度だけ検知する。
    const onTransitionEndOnce = (element, propertyName, callback) => {
      element.addEventListener(
        'transitionend',
        (event) => {
          if (event.propertyName === propertyName) {
            callback();
          }
        },
        { once: true },
      );
    };

    const initializePcIntro = () => {
      const track = introSection.querySelector('[data-jungle-moc-2026-intro-images]');
      const images = track ? track.querySelectorAll('[data-jungle-moc-2026-intro-image]') : [];
      if (!track || images.length === 0) {
        finishIntro();
        return;
      }

      onLastImageFadeIn(images, () => {
        onTransitionEndOnce(track, 'height', finishIntro);
        track.classList.add('is-docked');
      });
    };

    const initializeSpIntro = () => {
      // row2の退出はrow3の退出アニメーションの途中（duration半分）で開始する。
      const rowExitOverlapDuration = 350;

      const rowsContainer = introSection.querySelector('[data-jungle-moc-2026-intro-rows]');
      const rows = rowsContainer ? rowsContainer.querySelectorAll('[data-jungle-moc-2026-intro-row]') : [];
      const images = rowsContainer ? rowsContainer.querySelectorAll('[data-jungle-moc-2026-intro-image]') : [];
      if (!rowsContainer || rows.length < 3 || images.length === 0) {
        finishIntro();
        return;
      }

      const [row1, row2, row3] = rows;

      const dockRow1 = () => {
        onTransitionEndOnce(row1, 'height', finishIntro);
        row1.classList.add('is-docked');
      };

      const exitRow2 = () => {
        onTransitionEndOnce(row2, 'opacity', dockRow1);
        row2.classList.add('is-exiting');
      };

      onLastImageFadeIn(images, () => {
        row3.classList.add('is-exiting');
        window.setTimeout(exitRow2, rowExitOverlapDuration);
      });
    };

    if (smallScreen.matches) {
      initializeSpIntro();
    } else {
      initializePcIntro();
    }
  };

  const initializeRevealAfterIntro = () => {
    const revealTargets = root.querySelectorAll('[data-jungle-moc-2026-reveal-after-intro]');
    if (revealTargets.length === 0) {
      root.classList.remove('is-intro-pending');
      return;
    }

    if (reducedMotion.matches) {
      root.classList.remove('is-intro-pending');
      return;
    }

    // イントロアニメーション実装側が完了時に root へ dispatch する契約のカスタムイベント。
    root.addEventListener(
      'jungle-moc-2026:intro-complete',
      () => {
        root.classList.remove('is-intro-pending');
      },
      { once: true },
    );
  };

  const initializeFadeIn = () => {
    const fadeInTargets = root.querySelectorAll('[data-jungle-moc-2026-fade-in]');
    if (fadeInTargets.length === 0 || reducedMotion.matches) {
      return;
    }

    const revealOnScroll = (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    };

    let observer = null;

    const observeFadeInTargets = () => {
      if (observer) {
        observer.disconnect();
      }

      observer = new IntersectionObserver(revealOnScroll, {
        rootMargin: smallScreen.matches ? '0px 0px -40px 0px' : '0px 0px -64px 0px',
        threshold: 0,
      });

      fadeInTargets.forEach((target) => {
        if (!target.classList.contains('is-visible')) {
          observer.observe(target);
        }
      });
    };

    observeFadeInTargets();
    smallScreen.addEventListener('change', observeFadeInTargets);
  };

  const initializeMovie = () => {
    const page = root.querySelector('[data-jungle-moc-2026-movie-page]');
    const trigger = root.querySelector('[data-jungle-moc-2026-movie-trigger]');
    const inlineVideo = root.querySelector('[data-jungle-moc-2026-movie-inline]');
    const modal = root.querySelector('[data-jungle-moc-2026-movie-modal]');
    const modalVideo = root.querySelector('[data-jungle-moc-2026-movie-modal-video]');
    const closeButton = root.querySelector('[data-jungle-moc-2026-movie-close]');
    const backdrop = root.querySelector('[data-jungle-moc-2026-movie-backdrop]');

    if (!page || !trigger || !inlineVideo || !modal || !modalVideo || !closeButton || !backdrop) {
      return;
    }

    let inlineIsVisible = false;
    let modalIsOpen = false;
    let scrollPosition = 0;
    let bodyStyles = null;
    let documentScrollBehavior = '';

    const playVideo = (video) => {
      try {
        const playPromise = video.play();
        if (playPromise) {
          playPromise.catch(() => {});
        }
      } catch {
        // The poster and native controls remain available if playback is rejected.
      }
    };

    const copyCurrentTime = (source, target) => {
      if (!Number.isFinite(source.currentTime)) {
        return;
      }

      const updateCurrentTime = () => {
        try {
          target.currentTime = source.currentTime;
        } catch {
          // Some browsers reject seeking until enough metadata has loaded.
        }
      };

      if (target.readyState >= window.HTMLMediaElement.HAVE_METADATA) {
        updateCurrentTime();
        return;
      }

      target.addEventListener('loadedmetadata', updateCurrentTime, { once: true });
    };

    const lockBackgroundScroll = () => {
      scrollPosition = window.scrollY;
      bodyStyles = {
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        overflow: document.body.style.overflow,
      };
      documentScrollBehavior = document.documentElement.style.scrollBehavior;

      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPosition}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    };

    const unlockBackgroundScroll = () => {
      if (!bodyStyles) {
        return;
      }

      document.body.style.position = bodyStyles.position;
      document.body.style.top = bodyStyles.top;
      document.body.style.width = bodyStyles.width;
      document.body.style.overflow = bodyStyles.overflow;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, scrollPosition);
      document.documentElement.style.scrollBehavior = documentScrollBehavior;
      bodyStyles = null;
    };

    const openModal = () => {
      if (modalIsOpen) {
        return;
      }

      modalIsOpen = true;
      inlineVideo.pause();
      copyCurrentTime(inlineVideo, modalVideo);
      modalVideo.muted = false;
      modal.hidden = false;
      page.inert = true;
      lockBackgroundScroll();
      closeButton.focus();
      playVideo(modalVideo);
    };

    const closeModal = () => {
      if (!modalIsOpen) {
        return;
      }

      copyCurrentTime(modalVideo, inlineVideo);
      modalVideo.pause();
      modal.hidden = true;
      page.inert = false;
      modalIsOpen = false;
      unlockBackgroundScroll();
      trigger.focus();

      if (inlineIsVisible && !reducedMotion.matches) {
        playVideo(inlineVideo);
      }
    };

    const trapModalFocus = (event) => {
      if (!modalIsOpen || event.key !== 'Tab') {
        return;
      }

      const focusableElements = [closeButton, modalVideo];
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    const handleDocumentKeydown = (event) => {
      if (!modalIsOpen) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      trapModalFocus(event);
    };

    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        inlineIsVisible = entry.intersectionRatio >= 0.25;

        if (inlineIsVisible && !reducedMotion.matches && !modalIsOpen) {
          playVideo(inlineVideo);
        } else {
          inlineVideo.pause();
        }
      },
      { threshold: [0, 0.25] },
    );

    const handleReducedMotionChange = (event) => {
      if (event.matches) {
        inlineVideo.pause();
      } else if (inlineIsVisible && !modalIsOpen) {
        playVideo(inlineVideo);
      }
    };

    inlineVideo.muted = true;
    inlineVideo.defaultMuted = true;
    visibilityObserver.observe(trigger);
    trigger.addEventListener('click', openModal);
    closeButton.addEventListener('click', closeModal);
    backdrop.addEventListener('click', closeModal);
    document.addEventListener('keydown', handleDocumentKeydown);
    reducedMotion.addEventListener('change', handleReducedMotionChange);
  };

  initializePageEntry();
  initializeRevealAfterIntro();
  initializeIntroImages();
  initializeFadeIn();
  initializeMovie();
})();
